let accessToken = null;
let refreshPromise = null; // Prevent concurrent refresh calls

export const setAccessToken = (token) => {
  accessToken = token;
};

export const getAccessToken = () => accessToken;

export const clearAccessToken = () => {
  accessToken = null;
};

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Single refresh function — deduplicates concurrent calls
const refreshAccessToken = async () => {
  // If a refresh is already in-flight, wait for it
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setAccessToken(data.accessToken);
        return data.accessToken;
      }
      return null;
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

const api = async (path, options = {}) => {
  const { body, method = 'GET', headers = {}, ...rest } = options;

  const config = {
    method,
    headers: {
      ...headers,
    },
    credentials: 'include',
    ...rest,
  };

  if (body && !(body instanceof FormData)) {
    config.headers['Content-Type'] = 'application/json';
    config.body = JSON.stringify(body);
  } else if (body) {
    config.body = body;
  }

  if (accessToken) {
    config.headers['Authorization'] = `Bearer ${accessToken}`;
  }

  let res = await fetch(`${API_BASE}${path}`, config);

  // If 401, attempt token refresh once (deduplicated)
  const hadAuthHeader = Boolean(config.headers['Authorization']);
  const isRefreshRequest = path === '/auth/refresh';

  // Only try refresh for requests that were actually authenticated
  if (res.status === 401 && !options._retried && hadAuthHeader && !isRefreshRequest) {
    const newToken = await refreshAccessToken();

    if (newToken) {
      config.headers['Authorization'] = `Bearer ${newToken}`;
      res = await fetch(`${API_BASE}${path}`, config);
    } else {
      clearAccessToken();
      window.dispatchEvent(new Event('auth:logout'));

      const error = new Error('Session expired');
      error.status = 401;
      throw error;
    }
  }

  const contentType = res.headers.get('content-type');
  const data = contentType && contentType.includes('application/json')
    ? await res.json()
    : null;

  if (!res.ok) {
    const error = new Error(data?.message || `HTTP ${res.status}`);
    error.status = res.status;
    error.errors = data?.errors;
    throw error;
  }

  return data;
};

// Authenticated file download (e.g. CSV export). Fetches with the access token,
// refreshes once on 401, then triggers a browser download of the response body.
export const apiDownload = async (path, filename) => {
  const doFetch = (token) => fetch(`${API_BASE}${path}`, {
    method: 'GET',
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  let res = await doFetch(accessToken);

  if (res.status === 401 && accessToken) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      res = await doFetch(newToken);
    } else {
      clearAccessToken();
      window.dispatchEvent(new Event('auth:logout'));
      throw new Error('Session expired');
    }
  }

  if (!res.ok) {
    throw new Error(`Download failed (HTTP ${res.status})`);
  }

  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export { refreshAccessToken };
export default api;
